/**
 * O painel de comparação facial na tela do exame (E87.F3 — ADR-0079 §E.1/§E.3; E90.F4).
 *
 * Desde a E90.F4 é o painel de referência do owner, numa tabela só: o
 * cabeçalho traz as **duas fotos dentro das colunas** delas, seguem as **nove
 * linhas fixas** — elemento, o que se observou em cada imagem, o grau daquela
 * linha — e a **CONCLUSÃO fecha a tabela**, no rodapé, abaixo de todas as linhas.
 *
 * **O grau descreve; ele não calcula** (emenda §E.2). A conclusão é o degrau
 * que o servidor gravou (`compatibilidade`, emendas E.9 e E.11), sem número.
 * Esta tela nunca soma, media ou ordena graus — nove linhas `alta` com apoio a
 * origem distinta mostram o apoio a origem distinta, e é isso que aparece.
 *
 * Painel vazio é estado legítimo (§2.6: as afirmações caem na discordância) e
 * aparece declarado, nunca como seção que some.
 */
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { useEffect, useState } from 'react';

import { baixarFotoDoSignatario, type GrupoDaFoto } from '@/lib/fotos_do_signatario';
import { type ConferenciaFacialParecerData } from '@/lib/conferencia_facial';

import { marcaSemMaioria, textoDaConclusao, textoDoMetodo } from './painelDaConferenciaTexto';

export { marcaSemMaioria, textoDaConclusao, textoDoMetodo };

const t = ptBR.exameEletronico.conferenciaFacial.painel;

/** A imagem de uma das duas fotos comparadas, buscada com o cabeçalho de sessão. */
function FotoComparada({
  caseId,
  fotoId,
  legenda,
  grupo,
}: {
  caseId: string;
  fotoId: string;
  legenda: string;
  grupo: GrupoDaFoto;
}): JSX.Element {
  const client = useApiClient();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    let criada: string | null = null;
    void (async () => {
      try {
        const blob = await baixarFotoDoSignatario(client, caseId, fotoId);
        if (cancelado) return;
        criada = URL.createObjectURL(blob);
        setUrl(criada);
      } catch {
        if (!cancelado) setUrl(null);
      }
    })();
    return () => {
      cancelado = true;
      if (criada !== null) URL.revokeObjectURL(criada);
    };
  }, [client, caseId, fotoId]);

  return (
    <figure className="m-0 mt-sc-2" data-testid={`conferencia-imagem-${grupo}`}>
      <div className="flex h-40 items-center justify-center overflow-hidden border border-border bg-surface-2">
        {url === null ? null : (
          <img src={url} alt={legenda} className="h-full w-full object-contain" />
        )}
      </div>
      <figcaption className="sr-only">{legenda}</figcaption>
    </figure>
  );
}

export interface PainelDaConferenciaProps {
  caseId: string;
  parecer: ConferenciaFacialParecerData;
}

export function PainelDaConferencia({ caseId, parecer }: PainelDaConferenciaProps): JSX.Element {
  const conclusao = (
    <div className="space-y-sc-1">
      <p className="text-body-sm text-fg" data-testid="conferencia-conclusao">
        <span className="font-semibold">{t.conclusao}: </span>
        {textoDaConclusao(parecer)}
      </p>
      {parecer.compatibilidade === null ? null : (
        <p className="text-caption text-fg-muted" data-testid="conferencia-conclusao-ressalva">
          {t.conclusaoRessalva}
        </p>
      )}
    </div>
  );

  if (parecer.painel.length === 0) {
    return (
      <div className="space-y-sc-3" data-testid="conferencia-painel">
        <h4 className="text-body-sm font-semibold uppercase text-fg">{t.titulo}</h4>
        <p
          className="max-w-prose text-body-sm text-fg-muted"
          data-testid="conferencia-painel-vazio"
        >
          {t.semPainel}
        </p>
        {conclusao}
      </div>
    );
  }

  return (
    <div className="space-y-sc-3" data-testid="conferencia-painel">
      <h4 className="text-body-sm font-semibold uppercase text-fg">{t.titulo}</h4>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-border text-body-sm">
          <thead>
            <tr className="border-b border-border text-left text-caption text-fg-muted">
              <th scope="col" className="border-r border-border p-sc-2 align-top font-normal">
                {t.colunaElemento}
              </th>
              <th scope="col" className="border-r border-border p-sc-2 align-top font-normal">
                {t.colunaSelfie}
                <FotoComparada
                  caseId={caseId}
                  fotoId={parecer.par.ccb.foto_id}
                  grupo="ccb"
                  legenda={t.fotoFormalizacao}
                />
              </th>
              <th scope="col" className="border-r border-border p-sc-2 align-top font-normal">
                {t.colunaIdentidade}
                <FotoComparada
                  caseId={caseId}
                  fotoId={parecer.par.documento_identidade.foto_id}
                  grupo="documento_identidade"
                  legenda={t.fotoIdentidade}
                />
              </th>
              <th scope="col" className="p-sc-2 align-top font-normal">
                {t.colunaGrau}
              </th>
            </tr>
          </thead>
          <tbody>
            {parecer.painel.map((linha) => (
              <tr
                key={linha.elemento}
                className="border-b border-border align-top"
                data-testid={`painel-linha-${linha.elemento}`}
              >
                <th
                  scope="row"
                  className="border-r border-border p-sc-2 text-left font-semibold text-fg"
                >
                  {t.elementos[linha.elemento]}
                </th>
                <td className="border-r border-border p-sc-2 text-fg-muted">{linha.selfie}</td>
                <td className="border-r border-border p-sc-2 text-fg-muted">{linha.identidade}</td>
                <td
                  className="p-sc-2 text-center font-semibold text-fg"
                  data-testid={`painel-grau-${linha.elemento}`}
                >
                  {t.graus[linha.grau]}
                  <span className="block text-caption font-normal text-fg-dim">
                    {marcaSemMaioria(parecer, linha.elemento)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border">
              <td colSpan={4} className="p-sc-2">
                {conclusao}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="max-w-prose text-caption text-fg-dim" data-testid="conferencia-legenda">
        {t.legendaDosGraus}
      </p>
      <p className="max-w-prose text-caption text-fg-dim">{t.nota}</p>
      <p className="max-w-prose text-caption text-fg-dim" data-testid="conferencia-metodo">
        {textoDoMetodo(parecer)}
      </p>
    </div>
  );
}
